/**
 * Quem aparece primeiro na lista de pessoas (SPEC-20260923-2315).
 *
 * Abrir a lista inteira num workspace grande obriga a procurar com o olho o
 * mesmo punhado de nomes toda vez. Quem se trabalha junto é sempre parecido,
 * então a lista abre curta: quem já está marcado, mais os últimos que esta
 * pessoa usou, e o resto só aparece ao digitar.
 *
 * A memória fica no navegador, como as outras preferências de quem trabalha
 * (aparelhos da chamada, agrupamento do quadro, conversão de item). É hábito
 * pessoal, não dado do workspace.
 */

export type Pessoa = { id: string; name: string }

const CHAVE = 'orbind.pessoas.recentes'
/** Quantas guardar: o suficiente para cobrir o time de perto, sem virar lista. */
const QUANTAS_GUARDAR = 20
/** Quantas mostrar antes de digitar. */
export const QUANTAS_SEM_BUSCA = 5

export function lerRecentes(): string[] {
  try {
    const cru = localStorage.getItem(CHAVE)
    if (!cru) return []
    const lido: unknown = JSON.parse(cru)
    return Array.isArray(lido) ? lido.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

/** Marcar alguém põe essa pessoa na frente — a mais recente é a primeira. */
export function registrarUso(memberId: string): void {
  try {
    const atual = lerRecentes().filter((id) => id !== memberId)
    localStorage.setItem(CHAVE, JSON.stringify([memberId, ...atual].slice(0, QUANTAS_GUARDAR)))
  } catch {
    /* sem storage: a lista só não aprende */
  }
}

/**
 * A ordem da lista: MARCADOS primeiro, depois os recentes, depois o resto.
 *
 * Os marcados vêm na frente porque a lista também serve para DESMARCAR — quem
 * já é membro sumir atrás de uma busca seria pior do que a lista longa que
 * isto veio resolver.
 */
export function ordenarPessoas(
  pessoas: readonly Pessoa[],
  marcados: ReadonlySet<string>,
  recentes: readonly string[],
): Pessoa[] {
  const posicaoRecente = new Map(recentes.map((id, i) => [id, i]))
  const peso = (p: Pessoa) => (marcados.has(p.id) ? 0 : posicaoRecente.has(p.id) ? 1 : 2)
  return [...pessoas].sort((a, b) => {
    const pa = peso(a)
    const pb = peso(b)
    if (pa !== pb) return pa - pb
    if (pa === 1) return posicaoRecente.get(a.id)! - posicaoRecente.get(b.id)!
    return a.name.localeCompare(b.name, 'pt-BR')
  })
}

/** Acento não pode atrapalhar: "jo" acha "João". */
function semAcento(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
}

export function filtrarPessoas<T extends Pessoa>(pessoas: readonly T[], busca: string): T[] {
  const termo = semAcento(busca.trim())
  if (termo === '') return [...pessoas]
  return pessoas.filter((p) => semAcento(p.name).includes(termo))
}

/**
 * Quantas linhas mostrar sem busca.
 *
 * Nunca menos que os marcados: esconder alguém que já está no item deixaria a
 * pessoa sem como tirá-lo sem adivinhar o nome.
 */
export function quantasMostrar(marcados: number): number {
  return Math.max(QUANTAS_SEM_BUSCA, marcados)
}
